import classNames from 'classnames'
import { AnimatePresence, Variants, motion } from 'framer-motion'
import { Ring2 } from 'ldrs/react'
import React, { PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import 'ldrs/react/Ring2.css'

import styles from './Loading.module.scss'

export interface LoadingProps {
  isLoading: boolean
  text?: string
  className?: string
  showLoadingIcon?: boolean
  fullWidth?: boolean
  asOverlay?: boolean
  renderText?: () => ReactNode
  disableIconAnimation?: boolean
  wrapperClass?: string
}

export const useLoadingDots = (isActive = true): string => {
  const [index, setIndex] = useState(0)
  const patterns = ['.', '..', '...', '..']

  useEffect(() => {
    if (!isActive) return
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % patterns.length)
    }, 250)
    return () => clearInterval(interval)
  }, [isActive, patterns.length])

  return patterns[index]
}

const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

export const Loading: React.FunctionComponent<PropsWithChildren<LoadingProps>> = ({
  isLoading,
  text,
  showLoadingIcon,
  asOverlay = false,
  renderText,
  children,
  className,
  fullWidth,
  disableIconAnimation,
  wrapperClass,
}) => {
  const { t } = useTranslation()

  const LoaderElement = (
    <motion.div
      key='loader'
      variants={fadeVariants}
      initial='initial'
      animate='animate'
      exit='exit'
      transition={{ duration: 0.2 }}
      className={classNames(styles.wrapper, wrapperClass, {
        [styles.fullWidth]: fullWidth,
      })}
    >
      {showLoadingIcon && (
        <div className={classNames(styles.icon)}>
          <Ring2
            size='50'
            stroke='5'
            strokeLength='0.25'
            bgOpacity='0.1'
            speed={disableIconAnimation ? '0' : '0.8'}
            color='#FF0044'
          />
        </div>
      )}
      {renderText ? (
        renderText()
      ) : (
        <div className={className}>
          <p>{text ?? t('Loading...')}</p>
        </div>
      )}
    </motion.div>
  )

  if (asOverlay) {
    return (
      <div className={styles.overlay}>
        <AnimatePresence>{isLoading && LoaderElement}</AnimatePresence>
        {children}
      </div>
    )
  }

  return (
    <AnimatePresence mode='wait'>
      {isLoading ? LoaderElement : <React.Fragment key='content'>{children}</React.Fragment>}
    </AnimatePresence>
  )
}
